import React from "react";
import { Link } from "react-router-dom";
import { Bookmark, MapPin, Star, ArrowUpRight } from "@phosphor-icons/react";
import { formatMoney } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";

const Chip = ({ children, tone = "default" }) => {
  const tones = {
    default: "bg-white/10 text-white backdrop-blur-md ring-1 ring-white/15",
    accent: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200/60 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800/50",
    subtle: "bg-secondary text-foreground/80 ring-1 ring-border",
    gold: "bg-gradient-to-b from-amber-100 to-amber-200 text-amber-900 ring-1 ring-amber-300/70 dark:from-amber-500/25 dark:to-amber-600/15 dark:text-amber-200 dark:ring-amber-500/40",
    slate: "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900",
  };
  return <span className={`inline-flex items-center px-2 py-[3px] rounded-md text-[10px] font-bold tracking-[0.06em] uppercase ${tones[tone]}`}>{children}</span>;
};

export default function CompanyCard({ company, onToggleBookmark }) {
  const toggleBookmark = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const r = await api.post(`/bookmarks/${company.id}`);
      onToggleBookmark && onToggleBookmark(company.id, r.data.bookmarked);
      toast.success(r.data.bookmarked ? "Bookmarked" : "Removed from bookmarks");
    } catch {
      toast.error("Failed to update bookmark");
    }
  };

  const isFeatured = !!company.featured;
  const isHigh = company.priority === "High";

  return (
    <Link
      to={`/companies/${company.id}`}
      data-testid={`company-card-${company.id}`}
      className={`group relative block rounded-2xl overflow-hidden bg-card border transition-all duration-500 animate-fade-in-up
        ${isFeatured ? "border-transparent shadow-[0_0_0_1px_hsl(var(--border))]" : "border-border"}
        hover:-translate-y-1 hover:shadow-[0_25px_60px_-15px_rgba(15,23,42,0.25),0_10px_25px_-15px_rgba(15,23,42,0.1)]
        dark:hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8),0_10px_25px_-15px_rgba(0,0,0,0.5)]`}
    >
      {/* Featured accent strip */}
      {isFeatured && (
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-600 z-20" />
      )}

      {/* Banner */}
      <div className="relative h-44 overflow-hidden bg-slate-100 dark:bg-slate-800">
        {company.banner_url && (
          <img
            src={company.banner_url}
            alt=""
            className="h-full w-full object-cover group-hover:scale-[1.08] transition-transform duration-[900ms] ease-out"
          />
        )}
        {/* Rich multi-stop gradient for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-900/25 to-transparent" />
        {/* Subtle grain */}
        <div
          className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.6 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
        />

        {/* Top-left status chips */}
        <div className="absolute top-3 left-3 flex gap-1.5 z-10">
          {isFeatured && <Chip tone="gold"><Star size={9} weight="fill" className="mr-1" />Featured</Chip>}
          {isHigh && <Chip tone="default">Priority</Chip>}
        </div>

        {/* Bookmark */}
        <button
          onClick={toggleBookmark}
          data-testid={`bookmark-btn-${company.id}`}
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm grid place-items-center hover:scale-110 transition-transform shadow-md z-10"
          aria-label="Bookmark"
        >
          <Bookmark size={15} weight={company.is_bookmarked ? "fill" : "regular"} className={company.is_bookmarked ? "text-emerald-600" : "text-slate-700 dark:text-slate-200"} />
        </button>

        {/* Bottom-left: stage + industry chips */}
        <div className="absolute bottom-3 left-3 flex gap-1.5 z-10">
          <Chip tone="default">{company.stage}</Chip>
          <Chip tone="default">{company.industry}</Chip>
        </div>

        {/* Hover: view deal pill */}
        <div className="absolute bottom-3 right-3 z-10 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
          <div className="inline-flex items-center gap-1 pl-3 pr-2 h-8 rounded-full bg-white text-slate-900 text-[11px] font-bold uppercase tracking-wider shadow-lg">
            View deal <ArrowUpRight size={12} weight="bold" />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-6 pt-8 relative">
        {/* Floating logo */}
        <div className="absolute -top-8 left-6 h-14 w-14 rounded-xl bg-card overflow-hidden ring-1 ring-border shadow-[0_10px_30px_-5px_rgba(15,23,42,0.15)]">
          <div className="h-full w-full bg-secondary overflow-hidden">
            {company.logo_url ? (
              <img src={company.logo_url.startsWith("http") ? company.logo_url : `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full grid place-items-center text-lg font-display font-bold text-muted-foreground">{company.name?.[0]}</div>
            )}
          </div>
        </div>

        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-display text-xl font-bold tracking-tight leading-tight truncate">{company.name}</h3>
        </div>
        <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground mt-1">
          <MapPin size={11} />
          <span>{company.location || company.country}</span>
          {company.region && company.region !== "Outside UK" && (
            <>
              <span className="text-border">·</span>
              <span>{company.region}</span>
            </>
          )}
        </div>

        <p className="mt-4 text-[14px] text-foreground/80 line-clamp-2 leading-relaxed min-h-[2.6rem]">{company.tagline}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          <Chip tone="subtle">{company.revenue_status}</Chip>
          {company.eis && <Chip tone="accent">EIS</Chip>}
          {company.seis && <Chip tone="accent">SEIS</Chip>}
        </div>

        {/* Bottom strip — Raising · Stage · Eligibility (SEIS/EIS) */}
        <div className="mt-5 pt-5 border-t border-border grid grid-cols-3 gap-3">
          <div>
            <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground font-bold">Raising</div>
            <div className="num-display font-display font-bold text-xl mt-1 tracking-tight text-emerald-700 dark:text-emerald-400">
              {formatMoney(company.fundraising?.amount_raising)}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground font-bold">Stage</div>
            <div className="font-display font-bold text-base mt-1 tracking-tight leading-tight" data-testid="card-stage">
              {company.stage || "—"}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground font-bold">Eligibility</div>
            <div className="mt-1 flex flex-wrap gap-1" data-testid="card-eligibility">
              {company.seis && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-[10px] font-bold tracking-wide">
                  SEIS
                </span>
              )}
              {company.eis && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-[10px] font-bold tracking-wide">
                  EIS
                </span>
              )}
              {!company.seis && !company.eis && (
                <span className="text-muted-foreground text-xs">—</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
